import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Side, MarginType } from '@/common/constants';
import { z } from 'zod';
import type { SizeUnit } from '@/feature/order_book/header/size_unit_toggle';
import { OrderType, TiF, SLTPType } from '@/feature/order_entry/order_types';

const isPositiveNumericString = (value: string): boolean => {
  if (!value || value.trim() === '') return false;
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

const VALIDATION_MESSAGES = {
  positiveNumber: msg({ comment: 'Order form validation', message: 'Must be a positive number' }),
  sizeRequired: msg({ comment: 'Order form validation', message: 'Please enter order size' }),
  sizePositive: msg({ comment: 'Order form validation', message: 'Order size must be greater than zero' }),
  priceRequired: msg({ comment: 'Order form validation', message: 'Please enter order price' }),
  pricePositive: msg({ comment: 'Order form validation', message: 'Order price must be greater than zero' }),
  triggerRequired: msg({ comment: 'Order form validation', message: 'Please enter trigger price' }),
  triggerPositive: msg({ comment: 'Order form validation', message: 'Trigger price must be greater than zero' }),
  validationFailed: msg({ comment: 'Order form validation fallback', message: 'Validation failed' }),
};

export type TranslateValidationMessage = (descriptor: MessageDescriptor) => string;

// English fallback for non-React callers (tests, type derivation). UI callers must pass the
// active locale's `t` and rebuild the schema on locale change — zod bakes messages in at creation.
const englishFallback: TranslateValidationMessage = (descriptor) => descriptor.message ?? '';

export const createOrderFormSchema = (t: TranslateValidationMessage = englishFallback) => {
  const coercedOptionalPositiveNumber = z
    .union([
      z.number().positive(),
      z
        .string()
        .refine((val) => val === '' || isPositiveNumericString(val), { message: t(VALIDATION_MESSAGES.positiveNumber) }),
      z.undefined(),
    ])
    .optional()
    .transform((val) => (val === '' || val == null ? undefined : val));

  const CommonOrderFields = z.object({
    side: z.nativeEnum(Side),
    marginType: z.nativeEnum(MarginType),
    size: z
      .string()
      .min(1, { message: t(VALIDATION_MESSAGES.sizeRequired) })
      .refine(isPositiveNumericString, { message: t(VALIDATION_MESSAGES.sizePositive) }),
    sizeUnit: z.enum(['asset', 'quote'] as const satisfies SizeUnit[]),
    reduceOnly: z.boolean().optional(),
    orderSlider: z.coerce.number().int(),
    leverage: z.coerce.number(),
    marketId: z.number().optional(),
    triggerCondition: z.number().optional(),
    linkedPositionId: z.number().optional(),
    // TP/SL Fields
    tpSLEnabled: z.boolean().optional(),
    takeProfit: coercedOptionalPositiveNumber,
    stopLoss: coercedOptionalPositiveNumber,
    tpType: z.nativeEnum(SLTPType).optional(),
    slType: z.nativeEnum(SLTPType).optional(),
  });

  const MarketOrderSchema = z.object({
    orderType: z.literal(OrderType.market),
    price: z.string().optional(),
    tif: z.nativeEnum(TiF).optional(),
  });

  const LimitOrderSchema = z.object({
    orderType: z.literal(OrderType.limit),
    price: z
      .string()
      .min(1, { message: t(VALIDATION_MESSAGES.priceRequired) })
      .refine(isPositiveNumericString, { message: t(VALIDATION_MESSAGES.pricePositive) }),
    tif: z.nativeEnum(TiF),
  });

  const StopMarketOrderSchema = z.object({
    orderType: z.literal(OrderType.stopMarket),
    price: z
      .string()
      .optional()
      .refine((value) => value == null || value === '' || isPositiveNumericString(value), {
        message: t(VALIDATION_MESSAGES.pricePositive),
      }),
    tif: z.nativeEnum(TiF).optional(),
    stopPrice: z
      .string()
      .min(1, { message: t(VALIDATION_MESSAGES.triggerRequired) })
      .refine(isPositiveNumericString, { message: t(VALIDATION_MESSAGES.triggerPositive) }),
  });

  const StopLimitOrderSchema = z.object({
    orderType: z.literal(OrderType.stopLimit),
    price: z
      .string()
      .min(1, { message: t(VALIDATION_MESSAGES.priceRequired) })
      .refine(isPositiveNumericString, { message: t(VALIDATION_MESSAGES.pricePositive) }),
    tif: z.nativeEnum(TiF),
    stopPrice: z
      .string()
      .min(1, { message: t(VALIDATION_MESSAGES.triggerRequired) })
      .refine(isPositiveNumericString, { message: t(VALIDATION_MESSAGES.triggerPositive) }),
  });

  return z.union([MarketOrderSchema, LimitOrderSchema, StopLimitOrderSchema, StopMarketOrderSchema]).and(CommonOrderFields);
};

export const OrderFormSchema = createOrderFormSchema();

export type OrderFormData = z.input<typeof OrderFormSchema>;

export type OrderValidationFunction = (formData: Partial<OrderFormData>) => string | null;

export const createOrderFormSchemaWithValidation = (
  validateOrder?: OrderValidationFunction,
  t: TranslateValidationMessage = englishFallback
) => {
  const schema = createOrderFormSchema(t);

  if (!validateOrder) {
    return schema;
  }

  return schema.refine(
    (data) => {
      return !validateOrder(data);
    },
    (data) => ({
      message: validateOrder(data) || t(VALIDATION_MESSAGES.validationFailed),
      // Not `['root']`: react-hook-form `unset`s `errors.root` before its validity check, so a
      // failing order looked valid and onSubmit fired with the resolver's empty `{}` values.
      path: ['orderValidation'],
    })
  );
};
